// SPDX-License-Identifier: AGPL-3.0-or-later

import {StatusSlate} from '@app/features/app/components/dialogs/shared/StatusSlate';
import {PREMIUM_PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import {useFormSubmit} from '@app/features/app/hooks/useFormSubmit';
import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import {openClaimAccountModal} from '@app/features/auth/components/modals/ClaimAccountModal';
import {GiftSendToFriendModal} from '@app/features/expressions/components/modals/GiftSendToFriendModal';
import type {GiftMetadata} from '@app/features/gift/commands/GiftCommands';
import * as GiftCommands from '@app/features/gift/commands/GiftCommands';
import {getGiftDurationText} from '@app/features/gift/utils/GiftUtils';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {ComponentDispatch} from '@app/features/platform/utils/ComponentBus';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as TextCopyCommands from '@app/features/ui/commands/TextCopyCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Form} from '@app/features/ui/components/form/Form';
import {Input} from '@app/features/ui/components/form/Input';
import {Spinner} from '@app/features/ui/components/Spinner';
import * as UserCommands from '@app/features/user/commands/UserCommands';
import styles from '@app/features/user/components/modals/tabs/GiftInventoryTab.module.css';
import Users from '@app/features/user/state/Users';
import {getFormattedShortDate} from '@app/features/user/utils/dateFormatting';
import {UserPremiumTypes} from '@app/vendor/constants/UserConstants';
import {Trans, useLingui} from '@lingui/react/macro';
import {
	CaretDownIcon,
	CheckIcon,
	CopyIcon,
	GiftIcon,
	NetworkSlashIcon,
	ShareNetworkIcon,
	WarningCircleIcon,
} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';

const logger = new Logger('GiftInventoryTab');
interface GiftCodeFormInputs {
	code: string;
}
interface GiftCardProps {
	gift: GiftMetadata;
	isExpanded: boolean;
	onToggle: () => void;
	onRedeemSuccess: () => void;
}
const GiftCard: React.FC<GiftCardProps> = observer(({gift, isExpanded, onToggle, onRedeemSuccess}) => {
	const {t, i18n} = useLingui();
	const currentUser = Users.currentUser;
	const [copied, setCopied] = useState(false);
	const [redeeming, setRedeeming] = useState(false);
	const giftUrl = `${RuntimeConfig.giftEndpoint}/${gift.code}`;
	const isLifetime = currentUser?.isPremium() && currentUser.premiumType === UserPremiumTypes.LIFETIME;
	const isRedeemed = !!gift.redeemed_at;
	const durationText = getGiftDurationText(i18n, gift);
	const handleCopy = async () => {
		try {
			await TextCopyCommands.copy(i18n, giftUrl, true);
			setCopied(true);
			ToastCommands.createToast({type: 'success', children: <Trans>Gift URL copied to clipboard!</Trans>});
			setTimeout(() => setCopied(false), 2000);
		} catch (error) {
			logger.error('Failed to copy gift URL', error);
			ToastCommands.createToast({type: 'error', children: <Trans>Failed to copy URL</Trans>});
		}
	};
	const handleRedeem = async () => {
		setRedeeming(true);
		try {
			await GiftCommands.redeem(i18n, gift.code);
			ToastCommands.createToast({
				type: 'success',
				children: <Trans>Gift redeemed successfully! Enjoy your {PREMIUM_PRODUCT_NAME}!</Trans>,
			});
			onRedeemSuccess();
		} catch (error) {
			logger.error('Failed to redeem gift', error);
			ToastCommands.createToast({type: 'error', children: <Trans>Failed to redeem gift</Trans>});
		} finally {
			setRedeeming(false);
		}
	};
	const handleShare = useCallback(() => {
		ModalCommands.push(modal(() => <GiftSendToFriendModal code={gift.code} />));
	}, [gift.code]);
	useEffect(() => {
		const currentUser = Users.getCurrentUser();
		if (currentUser?.hasUnreadGiftInventory) {
			UserCommands.update({has_unread_gift_inventory: false});
		}
	}, []);
	return (
		<div className={styles.giftCard}>
			<button type="button" onClick={onToggle} className={styles.giftCardHeader}>
				<div className={clsx(styles.giftIcon, isRedeemed ? styles.giftIconRedeemed : styles.giftIconActive)}>
					<GiftIcon className={styles.giftIconImage} />
				</div>
				<div className={styles.giftInfo}>
					<h3 className={styles.giftTitle}>{durationText}</h3>
					<p className={styles.giftDate}>
						{isRedeemed ? (
							<Trans>Redeemed {getFormattedShortDate(new Date(gift.redeemed_at!))}</Trans>
						) : (
							<Trans>Purchased {getFormattedShortDate(new Date(gift.created_at))}</Trans>
						)}
					</p>
				</div>
				<CaretDownIcon weight="bold" className={clsx(styles.expandIcon, isExpanded && styles.expandIconRotated)} />
			</button>
			{isExpanded && (
				<div className={styles.giftCardContent}>
					<div className={styles.giftCardActions}>
						<div className={styles.giftUrlSection}>
							<Input
								id={`gift-url-${gift.code}`}
								label={t`Gift URL`}
								value={giftUrl}
								readOnly
								onClick={(e) => e.currentTarget.select()}
								rightElement={
									<Button
										compact
										fitContent
										onClick={handleCopy}
										leftIcon={copied ? <CheckIcon size={16} weight="bold" /> : <CopyIcon size={16} />}
									>
										{copied ? t`Copied` : t`Copy`}
									</Button>
								}
							/>
						</div>
						{isRedeemed && (
							<div className={styles.redeemedMessage}>
								<p className={styles.redeemedMessageText}>
									{gift.redeemed_by ? (
										<Trans>
											Redeemed by {gift.redeemed_by.username}#{gift.redeemed_by.discriminator}
										</Trans>
									) : (
										<Trans>This gift has been redeemed</Trans>
									)}
								</p>
							</div>
						)}
						{!isRedeemed && (
							<div className={styles.giftCardFooter}>
								{!isLifetime && (
									<Button variant="primary" onClick={handleRedeem} disabled={redeeming} submitting={redeeming}>
										<Trans>Redeem for Yourself</Trans>
									</Button>
								)}
								<Button
									variant="secondary"
									onClick={handleShare}
									leftIcon={<ShareNetworkIcon size={16} weight="bold" />}
								>
									<Trans>Share with a Friend</Trans>
								</Button>
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
});
const GiftInventoryTab: React.FC = observer(() => {
	const {t, i18n} = useLingui();
	const [gifts, setGifts] = useState<Array<GiftMetadata>>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [expandedGiftId, setExpandedGiftId] = useState<string | null>(null);
	const isUnclaimed = !(Users.currentUser?.isClaimed() ?? false);
	const giftCodeForm = useForm<GiftCodeFormInputs>({defaultValues: {code: ''}});
	const handleGiftCodeSubmit = useCallback(
		async (data: GiftCodeFormInputs) => {
			const trimmedCode = data.code.trim();
			if (!trimmedCode) return;
			await GiftCommands.redeem(i18n, trimmedCode);
			giftCodeForm.reset();
		},
		[giftCodeForm, i18n],
	);
	const {handleSubmit: handleGiftCodeSubmitForm, isSubmitting: isGiftCodeSubmitting} = useFormSubmit({
		form: giftCodeForm,
		onSubmit: handleGiftCodeSubmit,
		defaultErrorField: 'code',
	});
	const fetchGifts = useCallback(async () => {
		if (isUnclaimed) {
			setLoading(false);
			return;
		}
		try {
			setError(false);
			const userGifts = await GiftCommands.fetchUserGifts();
			setGifts(userGifts);
			setLoading(false);
		} catch (error) {
			logger.error('Failed to fetch user gifts', error);
			setError(true);
			setLoading(false);
		}
	}, [isUnclaimed]);
	useEffect(() => {
		fetchGifts();
	}, [fetchGifts]);
	const handleToggle = (code: string) => {
		setExpandedGiftId((prev) => (prev === code ? null : code));
	};
	const handleRedeemSuccess = () => {
		fetchGifts();
	};
	if (isUnclaimed) {
		return (
			<StatusSlate
				Icon={WarningCircleIcon}
				title={<Trans>Claim Your Account</Trans>}
				description={<Trans>Claim your account to redeem or manage {PREMIUM_PRODUCT_NAME} gift codes.</Trans>}
				actions={[
					{
						text: <Trans>Claim Account</Trans>,
						onClick: () => openClaimAccountModal({force: true}),
						variant: 'primary',
					},
				]}
			/>
		);
	}
	return (
		<div className={styles.container}>
			<div>
				<h2 className={styles.sectionHeader}>
					<Trans>Redeem a Gift</Trans>
				</h2>
				<p className={styles.sectionDescription}>
					<Trans>Enter a gift code to redeem {PREMIUM_PRODUCT_NAME} for your account.</Trans>
				</p>
				<Form form={giftCodeForm} onSubmit={handleGiftCodeSubmitForm} aria-label={t`Redeem gift code form`}>
					<div className={styles.redeemForm}>
						<div className={styles.redeemInput}>
							<Input
								{...giftCodeForm.register('code', {
									required: t`Gift code is required`,
									minLength: {
										value: 1,
										message: t`Gift code cannot be empty`,
									},
									maxLength: {
										value: 100,
										message: t`Gift code is too long`,
									},
								})}
								error={giftCodeForm.formState.errors.code?.message}
								label=""
								placeholder={t`Enter gift code...`}
								autoFocus={false}
								minLength={1}
								maxLength={100}
								required={true}
							/>
						</div>
						<Button
							type="submit"
							variant="primary"
							submitting={isGiftCodeSubmitting}
							disabled={!giftCodeForm.watch('code')?.trim()}
							className={styles.redeemButton}
						>
							<Trans>Redeem</Trans>
						</Button>
					</div>
				</Form>
			</div>
			<div>
				<h2 className={styles.sectionHeader}>
					<Trans>Your Purchased Gifts</Trans>
				</h2>
				<p className={styles.sectionDescriptionNoMargin}>
					<Trans>
						Manage your purchased {PREMIUM_PRODUCT_NAME} gift codes. Share the gift URL with someone special or redeem
						it for yourself!
					</Trans>
				</p>
			</div>
			{loading && (
				<div className={styles.loadingContainer}>
					<Spinner />
				</div>
			)}
			{error && (
				<StatusSlate
					Icon={NetworkSlashIcon}
					title={t`Failed to Load Gift Inventory`}
					description={t`Please try again later.`}
					actions={[
						{
							text: t`Retry`,
							onClick: fetchGifts,
							variant: 'primary',
						},
					]}
				/>
			)}
			{!loading && !error && gifts.length === 0 && (
				<StatusSlate
					Icon={GiftIcon}
					title={<Trans>No gifts yet</Trans>}
					description={
						<Trans>
							Purchase a {PREMIUM_PRODUCT_NAME} gift from the {PREMIUM_PRODUCT_NAME} tab to share with friends!
						</Trans>
					}
					actions={[
						{
							text: <Trans>Go to {PREMIUM_PRODUCT_NAME}</Trans>,
							onClick: () => ComponentDispatch.dispatch('USER_SETTINGS_TAB_SELECT', {tab: 'plutonium'}),
							variant: 'primary',
							fitContent: true,
						},
					]}
				/>
			)}
			{!loading && !error && gifts.length > 0 && (
				<div className={styles.giftsList}>
					{gifts.map((gift) => (
						<GiftCard
							key={gift.code}
							gift={gift}
							isExpanded={expandedGiftId === gift.code}
							onToggle={() => handleToggle(gift.code)}
							onRedeemSuccess={handleRedeemSuccess}
						/>
					))}
				</div>
			)}
		</div>
	);
});

export default GiftInventoryTab;
